import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { weatherQueryOptions } from './app/api'
import { V5App as App, createQueryClient } from './v5/App'
import { bootData, prefetchAfterPaint, startEngine } from './app/data/boot'
import { applyV5DeepLink } from './v5/shell/routing'
import { selectedPlace, useApp } from './v5/store'
import { primePicture } from './v5/zones/radar/picture'
import { checkFresh, markRendered, noteStaleBuild, replacedByNewerBuild, swapInProgress, watchForUpdates } from './app/lib/updateGate'
import { isFirstVisit } from './app/locate'
import { IS_MOCK } from './app/mockMode'
import { loadFirstVisitNote } from './app/v4/shell/firstVisitNote'
import { initOverlays, useUi } from './app/ui'
import './app/styles/index.css'

// Speed first: the engine worker boots before React renders (index.html's boot script has
// usually started it already), and the last saved reports go into the query cache so the
// first paint shows weather, not a skeleton. A slow IndexedDB never holds the paint back
// more than RENDER_WAIT_MS.
// iOS Safari applies :active (the press feedback of every control) only when a touch listener exists.
document.addEventListener('touchstart', () => {}, { passive: true })
startEngine()
// A sheet link (/#wind, a reload with a sheet open) opens over its view.
initOverlays(false) // v5 uses the same places plane on desktop and phone; #places is a real deep link.
// A first visit opens the visitor's approximate area (no permission prompt); decided before
// the first query so no one else's default city is fetched. Checked before anything saves.
const firstVisit = isFirstVisit()
// A widget tap (/?lat=&lon=) selects its place before the first query is issued.
applyV5DeepLink()
const located = firstVisit
  ? import('./app/firstVisit')
      .then(({ applyFirstVisit, fetchLocate }) =>
        fetchLocate().then(async (r) => {
          const notice = applyFirstVisit(r)
          // The note's code arrives with its notice (before the first render), not after it.
          if (notice) await loadFirstVisitNote()
          useUi.getState().setNotice(notice)
        }),
      )
      .catch(() => useUi.getState().setNotice({ kind: 'unavailable' }))
  : null
const client = createQueryClient()
const RENDER_WAIT_MS = 150
const place = selectedPlace(useApp.getState())
const hydrated = bootData(client, weatherQueryOptions).catch(() => undefined)
// A last-good map may join the first commit, but its IndexedDB read/decode never holds the forecast.
void primePicture(`${place.latitude.toFixed(3)},${place.longitude.toFixed(3)}`).catch(() => undefined)
// A returning visitor's page came from the service worker's cache: the network's word on the current build (started in
// index.html, landing during this boot) replaces the page with a newer deploy before it has painted anything.
const fresh = checkFresh()
// The subsets are preloaded in HTML. One bounded font-ready wait avoids a fallback-font flash.
const fonts = document.fonts ? Promise.race([
  Promise.all([document.fonts.load('400 16px "Manrope V5"'), document.fonts.load('400 48px "Instrument Serif V5"')]),
  new Promise((resolve) => setTimeout(resolve, 1200)),
]) : Promise.resolve()
void Promise.all([IS_MOCK ? hydrated : Promise.race([hydrated, new Promise((resolve) => setTimeout(resolve, RENDER_WAIT_MS))]), located, fonts]).then(async () => {
  if (await replacedByNewerBuild(fresh)) return
  markRendered()
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App client={client} />
    </StrictMode>,
  )
  prefetchAfterPaint()
  // Root removes the boot plate in its layout effect, with the complete first commit.
  // A touch screen's sky fade-up (index.html) belongs to the first mount only: a later remount (the map and back) must not replay it.
  window.setTimeout(() => document.documentElement.setAttribute('data-sky-up-done', ''), 1500)
  // My Location follows the device (silently, only when location is already allowed): its own
  // small chunk, started once the first frame is on its way — never on the paint path.
  if ('geolocation' in navigator && !IS_MOCK) {
    void import('./app/myLocation').then((m) => m.startMyLocation(client, { firstVisit })).catch(() => undefined)
  }
})

// Installable app: offline shell + cached build assets (production only; never in dev).
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  // Registered after the first complete forecast (or 10 s): a first visit's precache (the home
  // screen's lazy chunks and glyphs, ~380 kB gzipped) never competes with the forecast's own
  // downloads. Installed workers update on navigation regardless.
  watchForUpdates()
  let registered = false
  const register = () => {
    if (registered) return
    registered = true
    navigator.serviceWorker.register('/sw.js').catch(() => {
      /* optional enhancement */
    })
  }
  window.addEventListener('wf:report-full', () => setTimeout(register, 500), { once: true })
  window.addEventListener('load', () => setTimeout(register, 10_000))
  // A deploy has since removed a lazily loaded chunk this tab's build references. A chunk the
  // visitor just asked for (they tapped or clicked a moment ago) reloads once onto the fresh
  // build; an idle warm-up that failed only marks the tab for a swap at a quiet moment
  // (hidden, next view, next place) — never a reload out of the blue.
  // A successful load means this tab can recover from the next deploy too.
  window.addEventListener('load', () => {
    setTimeout(() => {
      try {
        sessionStorage.removeItem('wf-chunk-reload')
      } catch {
        /* private mode */
      }
    }, 5000)
  })
  window.addEventListener('vite:preloadError', () => {
    // Offline, a reload can't fetch the chunk either (its boundary shows its fallback); a page already being replaced must not start a second load.
    if (navigator.onLine === false || swapInProgress()) return
    noteStaleBuild()
    if (navigator.userActivation && !navigator.userActivation.isActive) return
    try {
      if (sessionStorage.getItem('wf-chunk-reload')) return
      sessionStorage.setItem('wf-chunk-reload', '1')
    } catch {
      return
    }
    location.reload()
  })
}
